import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Lock, Eye, EyeOff, LogIn, Mail, ArrowLeft, KeyRound, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/ui/Toast';
type View = 'login' | 'forgot' | 'verify-otp' | 'reset-password' | 'reset-success';
export function Login() {
  const [view, setView] = useState<View>('login');
  // Login state
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  // OTP state
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Reset password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const login = useAuthStore(state => state.login);
  const routerNavigate = useNavigate();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  useEffect(() => {
    if (isAuthenticated) {
      routerNavigate('/');
    }
  }, [isAuthenticated, routerNavigate]);
  const {
    showToast
  } = useToast();
  // Auto-focus first OTP input when view changes
  useEffect(() => {
    if (view === 'verify-otp') {
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  }, [view]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      if (userId && password) {
        login({
          id: '1',
          name: 'Admin User',
          email: 'admin@waseel.com',
          role: 'admin'
        });
        showToast({
          type: 'success',
          title: 'Welcome back',
          message: 'Successfully logged in to Waseel Jisr Portal'
        });
        routerNavigate('/');
      } else {
        throw new Error('Please enter both User ID and password');
      }
    } catch (err) {
      setError('Invalid User ID or password');
      showToast({
        type: 'error',
        title: 'Login Failed',
        message: 'Please check your credentials and try again'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      showToast({
        type: 'error',
        title: 'Email Required',
        message: 'Please enter your email address to receive the OTP.'
      });
      return;
    }
    setIsSendingOtp(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    setIsSendingOtp(false);
    setView('verify-otp');
    showToast({
      type: 'success',
      title: 'OTP Sent',
      message: `A one-time password has been sent to ${forgotEmail}`
    });
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otpDigits];
      digits.forEach((d, i) => {
        if (index + i < 6) newOtp[index + i] = d;
      });
      setOtpDigits(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
      return;
    }
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);
    setOtpError('');
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const handleVerifyOtp = async () => {
    const otp = otpDigits.join('');
    if (otp.length < 6) {
      setOtpError('Please enter the complete 6-digit OTP');
      return;
    }
    setIsVerifyingOtp(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsVerifyingOtp(false);
    // Mock: accept any 6-digit OTP
    setView('reset-password');
    showToast({
      type: 'success',
      title: 'OTP Verified',
      message: 'Please set your new password.'
    });
  };
  const handleResendOtp = async () => {
    setIsSendingOtp(true);
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSendingOtp(false);
    showToast({
      type: 'success',
      title: 'OTP Resent',
      message: `A new OTP has been sent to ${forgotEmail}`
    });
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  };
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    if (!newPassword || !confirmPassword) {
      setResetError('Please fill in both fields');
      return;
    }
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }
    setIsResetting(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    setIsResetting(false);
    setView('reset-success');
    showToast({
      type: 'success',
      title: 'Password Reset',
      message: 'Your password has been reset successfully.'
    });
  };
  const handleBackToLogin = () => {
    setView('login');
    setForgotEmail('');
    setError('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
  };
  const viewHeaders: Record<View, {
    title: string;
    subtitle: string;
  }> = {
    login: {
      title: 'Welcome Back',
      subtitle: 'Sign in to Waseel Jisr Portal'
    },
    forgot: {
      title: 'Forgot Password',
      subtitle: 'Enter your email to receive a one-time password'
    },
    'verify-otp': {
      title: 'Enter OTP',
      subtitle: `We've sent a code to ${forgotEmail}`
    },
    'reset-password': {
      title: 'Reset Password',
      subtitle: 'Create a new password for your account'
    },
    'reset-success': {
      title: 'Password Reset',
      subtitle: 'Your password has been updated successfully'
    }
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4" data-id="element-327">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="w-full max-w-md" data-id="element-328">
        {/* Logo Section */}
        <div className="text-center mb-8" data-id="element-329">
          <div className="inline-flex items-center justify-center gap-6 w-auto h-24 px-8 rounded-2xl bg-white shadow-sm border border-slate-100 mb-4" data-id="element-330">
            <img src="https://www.tawuniya.com/assets/tawuniya-logo-BFmobvJd.svg" alt="Tawuniya Logo" className="h-8 w-auto object-contain" data-id="element-331" />
            <div className="w-px h-8 bg-slate-200" data-id="element-332" />
            <img src="https://cdn.magicpatterns.com/uploads/vTCY5UH2YGZJtqWG4nFSrJ/Screenshot_52.png" alt="Innova Logo" className="h-8 w-auto object-contain" data-id="element-333" />
          </div>
          <AnimatePresence mode="wait" data-id="element-334">
            <motion.div key={view} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.15
          }} data-id="element-335">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-336">
                {viewHeaders[view].title}
              </h1>
              <p className="text-slate-500 mt-2" data-id="element-337">
                {viewHeaders[view].subtitle}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-338">
          <div className="p-8" data-id="element-339">
            <AnimatePresence mode="wait" data-id="element-340">
              {/* Login View */}
              {view === 'login' && <motion.form key="login-form" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 20
            }} transition={{
              duration: 0.2
            }} onSubmit={handleSubmit} className="space-y-6" data-id="element-341">
                  <div className="space-y-4" data-id="element-342">
                    <Input label="User ID" type="text" placeholder="Enter your User ID" value={userId} onChange={e => setUserId(e.target.value)} icon={<User className="w-4 h-4" data-id="element-344" />} error={error ? ' ' : undefined} autoComplete="username" required data-id="element-343" />
                    <div className="space-y-1.5" data-id="element-345">
                      <div className="relative" data-id="element-346">
                        <Input label="Password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} icon={<Lock className="w-4 h-4" data-id="element-348" />} error={error} autoComplete="current-password" required data-id="element-347" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[34px] text-slate-400 hover:text-slate-600 focus:outline-none" data-id="element-349">
                          {showPassword ? <EyeOff className="w-4 h-4" data-id="element-350" /> : <Eye className="w-4 h-4" data-id="element-351" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between" data-id="element-352">
                    <Checkbox label="Remember me" data-id="element-353" />
                    <button type="button" onClick={() => setView('forgot')} className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline focus:outline-none" data-id="element-354">
                      Forgot password?
                    </button>
                  </div>

                  <Button type="submit" className="w-full bg-primary-600 hover:bg-primary-700" size="lg" isLoading={isLoading} rightIcon={!isLoading && <LogIn className="w-4 h-4" data-id="element-356" />} data-id="element-355">
                    Sign In
                  </Button>

                  <div className="pt-4 border-t border-slate-100" data-id="element-357">
                    <p className="text-xs text-slate-400 text-center mb-3 uppercase font-bold tracking-wider" data-id="element-358">
                      Quick Login (Demo)
                    </p>
                    <div className="grid grid-cols-2 gap-3" data-id="element-359">
                      <Button type="button" variant="outline" size="sm" className="text-[10px] h-9 border-primary-100 hover:bg-primary-50 text-primary-700" onClick={() => {
                    setUserId('admin@waseel.com');
                    setPassword('password123');
                  }} data-id="element-360">
                        Admin Account
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="text-[10px] h-9 border-primary-100 hover:bg-primary-50 text-primary-700" onClick={() => {
                    setUserId('branch@waseel.com');
                    setPassword('password123');
                  }} data-id="element-361">
                        Branch Account
                      </Button>
                    </div>
                  </div>
                </motion.form>}

              {/* Forgot Password View */}
              {view === 'forgot' && <motion.form key="forgot-form" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} onSubmit={handleSendOtp} className="space-y-6" data-id="element-362">
                  <Input label="Email Address" type="email" placeholder="Enter your registered email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} icon={<Mail className="w-4 h-4" data-id="element-364" />} autoComplete="email" required data-id="element-363" />
                  <p className="text-xs text-slate-500" data-id="element-365">
                    We'll send a one-time password (OTP) to this email address
                    so you can reset your password.
                  </p>
                  <Button type="submit" className="w-full bg-primary-600 hover:bg-primary-700" size="lg" isLoading={isSendingOtp} rightIcon={!isSendingOtp && <Mail className="w-4 h-4" data-id="element-367" />} data-id="element-366">
                    Send OTP
                  </Button>
                  <button type="button" onClick={handleBackToLogin} className="w-full flex items-center justify-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-368">
                    <ArrowLeft className="w-4 h-4" data-id="element-369" />
                    Back to Sign In
                  </button>
                </motion.form>}

              {/* Verify OTP View */}
              {view === 'verify-otp' && <motion.div key="verify-otp" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-370">
                  <div className="flex justify-center" data-id="element-371">
                    <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center" data-id="element-372">
                      <CheckCircle2 className="w-7 h-7 text-emerald-600" data-id="element-373" />
                    </div>
                  </div>

                  <div className="text-center" data-id="element-374">
                    <p className="text-sm text-slate-700 font-medium" data-id="element-375">
                      OTP sent to
                    </p>
                    <p className="text-sm text-primary-600 font-semibold mt-0.5" data-id="element-376">
                      {forgotEmail}
                    </p>
                  </div>

                  {/* OTP Input */}
                  <div data-id="element-377">
                    <label className="block text-sm font-medium text-slate-700 text-center mb-3" data-id="element-378">
                      Enter 6-digit OTP
                    </label>
                    <div className="flex justify-center gap-2" data-id="element-379">
                      {otpDigits.map((digit, index) => <input key={index} ref={el => {
                    otpRefs.current[index] = el;
                  }} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} onPaste={e => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    handleOtpChange(0, pasted);
                  }} className={`w-11 h-12 text-center text-lg font-bold rounded-lg border ${otpError ? 'border-red-300 focus:ring-red-500' : digit ? 'border-primary-300 bg-primary-50' : 'border-slate-200'} focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all`} data-id="element-380" />)}
                    </div>
                    {otpError && <p className="text-xs text-red-600 text-center mt-2" data-id="element-381">
                        {otpError}
                      </p>}
                  </div>

                  <Button className="w-full bg-primary-600 hover:bg-primary-700" size="lg" onClick={handleVerifyOtp} isLoading={isVerifyingOtp} rightIcon={!isVerifyingOtp && <ShieldCheck className="w-4 h-4" data-id="element-383" />} data-id="element-382">
                    Verify OTP
                  </Button>

                  <div className="space-y-3" data-id="element-384">
                    <Button variant="outline" className="w-full" onClick={handleResendOtp} isLoading={isSendingOtp} data-id="element-385">
                      <KeyRound className="w-4 h-4 mr-2" data-id="element-386" />
                      Resend OTP
                    </Button>
                    <button type="button" onClick={handleBackToLogin} className="w-full flex items-center justify-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-387">
                      <ArrowLeft className="w-4 h-4" data-id="element-388" />
                      Back to Sign In
                    </button>
                  </div>
                </motion.div>}

              {/* Reset Password View */}
              {view === 'reset-password' && <motion.form key="reset-form" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} onSubmit={handleResetPassword} className="space-y-6" data-id="element-389">
                  <div className="space-y-4" data-id="element-390">
                    <div className="relative" data-id="element-391">
                      <Input label="New Password" type={showNewPassword ? 'text' : 'password'} placeholder="Minimum 8 characters" value={newPassword} onChange={e => {
                    setNewPassword(e.target.value);
                    setResetError('');
                  }} icon={<Lock className="w-4 h-4" data-id="element-393" />} error={resetError && !confirmPassword ? resetError : undefined} required data-id="element-392" />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-[34px] text-slate-400 hover:text-slate-600 focus:outline-none" data-id="element-394">
                        {showNewPassword ? <EyeOff className="w-4 h-4" data-id="element-395" /> : <Eye className="w-4 h-4" data-id="element-396" />}
                      </button>
                    </div>

                    <div className="relative" data-id="element-397">
                      <Input label="Confirm Password" type={showConfirmPassword ? 'text' : 'password'} placeholder="Re-enter your new password" value={confirmPassword} onChange={e => {
                    setConfirmPassword(e.target.value);
                    setResetError('');
                  }} icon={<Lock className="w-4 h-4" data-id="element-399" />} error={resetError} required data-id="element-398" />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-[34px] text-slate-400 hover:text-slate-600 focus:outline-none" data-id="element-400">
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" data-id="element-401" /> : <Eye className="w-4 h-4" data-id="element-402" />}
                      </button>
                    </div>
                  </div>

                  <div className="text-xs text-slate-500 space-y-1" data-id="element-403">
                    <p className={`flex items-center gap-1.5 ${newPassword.length >= 8 ? 'text-emerald-600' : ''}`} data-id="element-404">
                      <span className={`w-1.5 h-1.5 rounded-full ${newPassword.length >= 8 ? 'bg-emerald-500' : 'bg-slate-300'}`} data-id="element-405" />
                      At least 8 characters
                    </p>
                    <p className={`flex items-center gap-1.5 ${newPassword && newPassword === confirmPassword ? 'text-emerald-600' : ''}`} data-id="element-406">
                      <span className={`w-1.5 h-1.5 rounded-full ${newPassword && newPassword === confirmPassword ? 'bg-emerald-500' : 'bg-slate-300'}`} data-id="element-407" />
                      Passwords match
                    </p>
                  </div>

                  <Button type="submit" className="w-full bg-primary-600 hover:bg-primary-700" size="lg" isLoading={isResetting} rightIcon={!isResetting && <ShieldCheck className="w-4 h-4" data-id="element-409" />} data-id="element-408">
                    Reset Password
                  </Button>

                  <button type="button" onClick={handleBackToLogin} className="w-full flex items-center justify-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-410">
                    <ArrowLeft className="w-4 h-4" data-id="element-411" />
                    Back to Sign In
                  </button>
                </motion.form>}

              {/* Reset Success View */}
              {view === 'reset-success' && <motion.div key="reset-success" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="space-y-6 text-center" data-id="element-412">
                  <div className="flex justify-center" data-id="element-413">
                    <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center" data-id="element-414">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" data-id="element-415" />
                    </div>
                  </div>

                  <div data-id="element-416">
                    <p className="text-base font-semibold text-slate-900" data-id="element-417">
                      Password Updated
                    </p>
                    <p className="text-sm text-slate-500 mt-2" data-id="element-418">
                      Your password has been reset successfully. You can now
                      sign in with your new password.
                    </p>
                  </div>

                  <Button className="w-full bg-primary-600 hover:bg-primary-700" size="lg" onClick={handleBackToLogin} rightIcon={<LogIn className="w-4 h-4" data-id="element-420" />} data-id="element-419">
                    Sign In
                  </Button>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div className="bg-slate-50 px-8 py-4 border-t border-slate-100 text-center" data-id="element-421">
            <p className="text-xs text-slate-500" data-id="element-422">
              Protected by enterprise-grade security.
              <br data-id="element-423" />
              Authorized personnel only.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center" data-id="element-424">
          <p className="text-sm text-slate-400" data-id="element-425">
            &copy; {new Date().getFullYear()} Waseel Jisr Portal. All rights
            reserved.
          </p>
        </div>
      </motion.div>
    </div>;
}